import { Injectable, OnDestroy, SecurityContext } from "@angular/core";
import { Location } from "@angular/common";
import { DomSanitizer } from "@angular/platform-browser";
import { ActivatedRoute, ParamMap, Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { RouterHelpers, Utilities } from "@tylertech/shared-module";
import
{
    Facet, FacetType, ISearchCriteria, InputParameter, InputType, Bucket
} from "@tylertech/tyler-api-types";

const QueryParamDelimiter = "&";
const SearchFacetBucketDelimiter = "_or_";

@Injectable()
export class SearchUrlService implements OnDestroy
{
    private readonly destructor$ = new Subject<void>();
    private searchParams: Record<string, string>;

    constructor(
        private readonly location: Location,
        private readonly domSanitizer: DomSanitizer,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute)
    {
        activatedRoute.queryParamMap
            .pipe(takeUntil(this.destructor$))
            .subscribe((paramMap: ParamMap) =>
            {
                this.searchParams = {};
                paramMap.keys.forEach(
                    (paramName: string) =>
                    {
                        const paramValue = RouterHelpers.tryGetParam(paramMap, paramName);
                        this.searchParams[paramName] = paramValue;
                    });
            });
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    // TODO - this may cause a feedback loop between the activatedRoute.queryParam subscription....
    updateUrl<T = unknown>(searchCriteria: ISearchCriteria<T>): Promise<boolean>
    {
        const queryParams = Object.assign(
            searchCriteria.QueryString
                ? { QueryString: searchCriteria.QueryString }
                : {},
            this.getFacetQueryParams(searchCriteria.Facets),
            this.getParameterQueryParam(searchCriteria.Parameters));

        return this.router.navigate(
            [],
            {
                relativeTo: this.activatedRoute,
                queryParams,
            });
    }

    private getFacetQueryParams(facets: Array<Facet>): Record<string, string>
    {
        return facets.reduce(
            (facetList: Record<string, string>, facet: Facet) =>
            {
                // TODO - do we not want to have the ranged facet types in the url?
                if (facet.Type !== FacetType.Multiselect || !facet.IsVisible)
                {
                    return facetList;
                }

                const facetName = this.getFacetName(facet);
                if (!facetName)
                {
                    console.error(`Facet name could not be resolved`, facet);
                    return facetList;
                }

                const buckets = facet.Buckets.reduce(
                    (bucketList: Array<string>, bucket: Bucket): Array<string> =>
                    {
                        if (bucket.Selected)
                        {
                            bucketList.push(
                                bucket.HiddenKey
                                    ? `${bucket.Key}|h|${bucket.HiddenKey}`
                                    : `${bucket.Key}`);
                        }

                        return bucketList;
                    },
                    [])
                    .join(SearchFacetBucketDelimiter);

                if (buckets.length > 0)
                {
                    facetList[facetName] = buckets;
                }

                return facetList;
            },
            {});
    }

    private getParameterQueryParam(parameters: Array<InputParameter>): Record<string, string>
    {
        return parameters.reduce(
            (queryParams: Record<string, string>, parameter: InputParameter) =>
            {
                if (!parameter.IsVisible)
                {
                    return queryParams;
                }

                const paramName = this.getParameterName(parameter);
                if (!paramName)
                {
                    console.error(`Parameter name could not be resolved`, parameter);
                    return queryParams;
                }

                switch (parameter.InputType)
                {
                    case InputType.Date:
                        {
                            if (parameter.Value?.MinDate)
                            {
                                queryParams[`from${paramName}`] = parameter.Value.MinDate;
                            }

                            if (parameter.Value?.MaxDate)
                            {
                                queryParams[`to${paramName}`] = parameter.Value.MaxDate;
                            }

                            return queryParams;
                        }
                    case InputType.Decimal:
                    case InputType.Number:
                        {
                            if (parameter.Min)
                            {
                                queryParams[`from${paramName}`] = parameter.Min.toString();
                            }

                            if (parameter.Max)
                            {
                                queryParams[`to${paramName}`] = parameter.Max.toString();
                            }

                            return queryParams;
                        }
                    case InputType.Boolean:
                        queryParams[paramName] = `${parameter.Value === true}`;
                        break;
                    case InputType.Metadata:
                        // ignore for now but might be nice to support later
                        break;
                    default:
                        if (this.hasValue(parameter))
                        {
                            queryParams[paramName] = parameter.Value;
                        }
                        break;
                }

                return queryParams;
            },
            {});
    }

    /**
     * Determines if the given parameter has a value and it isn't a default value.
     */
    private hasValue(parameter: InputParameter): boolean
    {
        if (parameter.Value === undefined || parameter.Value === null)
        {
            return false;
        }

        switch (typeof parameter.Value)
        {
            case "string":
                return parameter.Value !== "";
            default:
                return true;
        }
    }

    updateCriteria<T = unknown>(searchCriteria: ISearchCriteria<T>): void
    {
        if (!searchCriteria)
        {
            return;
        }

        Object.entries(this.searchParams)
            .forEach(([paramName, paramValue]: [string, string]) =>
            {
                // TODO - parse queryParam and update this.searchCriteria.Facets and this.searchCriteria.parameters
                searchCriteria.Facets.forEach(
                    (facet: Facet) =>
                    {
                        this.updateFacet(facet, paramName, paramValue);
                    });

                searchCriteria.Parameters.forEach(
                    (parameter: InputParameter) =>
                    {
                        this.updateParameter(parameter, paramName, paramValue);
                    });
                if (paramName === "QueryString")
                {
                    const encodedPath = this.domSanitizer.sanitize(SecurityContext.URL, encodeURIComponent(paramValue));
                    searchCriteria.QueryString = decodeURIComponent(encodedPath);
                }
            });
    }

    private getFacetName(facet: Facet): string | undefined
    {
        return facet.Name || facet.IndexFieldName;
    }

    private getParameterName(parameter: InputParameter): string | undefined
    {
        return parameter.Label || parameter.FieldId;
    }

    private updateFacet(facet: Facet, paramName: string, paramValue: string): void
    {
        const facetName = this.getFacetName(facet);
        if (paramName !== facetName)
        {
            return;
        }

        facet.Buckets.forEach(bucket =>
        {
            bucket.Selected = false;
        });


        if (facet.Type !== FacetType.Multiselect)
        {
            console.error(`Unsupported search facet type; was ${FacetType[facet.Type]}`);
            return;
        }

        const parts = paramValue.split(SearchFacetBucketDelimiter);
        for (const part of parts)
        {
            // TODO - why are we sanitizing this? it came out of the url...
            const sanitizedPart = this.domSanitizer.sanitize(SecurityContext.URL, part)
                .replace(/&amp;/g, QueryParamDelimiter);
            const keys = sanitizedPart.split("|h|");

            this.setOrCreateBucket(facet, keys[0], keys[1]);
        }
    }

    private setOrCreateBucket(facet: Facet, key: string, hiddenKey?: string): void
    {
        if (!facet.Buckets)
        {
            facet.Buckets = [];
        }
        let bucketSet = false;
        for (const bucket of facet.Buckets)
        {
            if (bucket.Key === key)
            {
                bucketSet = true;
                bucket.Selected = true;
            }
        }
        if (bucketSet === false)
            facet.Buckets.push(<Bucket>{
                Key: key,
                HiddenKey: hiddenKey,
                Selected: true,
            });
    }

    private updateParameter(parameter: InputParameter, paramName: string, paramValue: string): void
    {
        const matches = /^(from|to)(.*)/.exec(paramName);
        if (matches)
        {
            const isFrom = matches[1] === "from";
            const parameterName = this.getParameterName(parameter);
            if (matches[2] !== parameterName)
            {
                return;
            }

            if (!parameter.Value)
            {
                parameter.Value = {};
            }

            if (parameter.InputType === InputType.Date)
            {
                const newDate = paramValue;
                if (isFrom)
                {
                    parameter.Value.MinDate = newDate;
                }
                else
                {
                    parameter.Value.MaxDate = newDate;
                }
            }
            else
            {
                if (isFrom)
                {
                    parameter.Min = Number.parseInt(paramValue);
                    parameter.Value.Min = Number.parseInt(paramValue);
                }
                else
                {
                    parameter.Max = Number.parseInt(paramValue);
                    parameter.Value.Max = Number.parseInt(paramValue);
                }
            }
        }
        else
        {
            const parameterName = this.getParameterName(parameter);
            if (paramName !== parameterName)
            {
                return;
            }

            if (parameter.InputType !== InputType.Boolean)
            {
                parameter.Value = undefined;
            }

            // TODO - why are we sanitizing this? it came out of the url...
            // TODO - why are we escaping this? it came out of the url, it's already been encoded...
            const val = SearchUrlService.escapeHtml(
                this.domSanitizer.sanitize(
                    SecurityContext.URL, paramValue));
            parameter.Value = parameter.InputType === InputType.Boolean
                // TODO - We guaranteed that `value` will always be a string... why are we checking if it could be something else?
                ? typeof val === "boolean"
                    ? val
                    : /true/i.test(val)
                : val;
        }
    }

    /** @deprecated AngularJs and Angular have resources that are more complete than this. */
    private static escapeHtml(value: string): string
    {
        const entityMap = {
            "&": "&amp;",
            "<": "&lt;",
            ">": "&gt;",
            '"': "&quot;",
            "'": "&#39;",
        };
        return String(value)
            .replace(/[&<>"'/]/g, (s) =>
            {
                return entityMap[s];
            });
    }
}
